import { Link, useNavigate } from "react-router-dom";
import "../Components/Login.css";
import { useEffect, useState, useRef } from "react";
import { useUser } from "../Components/Context/UserContext";
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * Login component that handles both user login and signup functionality
 * Features toggle between login/signup forms and password validation
 */
export function Login() {
  // Form input states
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // Password validation states
  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  
  // Authentication states
  const { login } = useUser();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState([]);
  const [loginStatus, setLoginStatus] = useState("idle"); // idle | logging | success
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Admin access functionality
  const [clickCount, setClickCount] = useState(0);
  const timeoutRef = useRef(null);

  // Password validation criteria
  const minLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  /**
   * Validates email format using regex
   * @param {string} email - Email to validate
   * @returns {boolean} True if email is valid
   */
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Effect to validate password whenever it changes
   * Checks all password requirements: length, uppercase, lowercase, number, special char
   */
  useEffect(() => {
    setPasswordValid(
      minLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar
    );
  }, [
    password,
    minLength,
    hasUppercase,
    hasLowercase,
    hasNumber,
    hasSpecialChar,
  ]);

  /**
   * Handles user signup form submission
   * Validates email and password before creating account
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email format
    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }

    // Validate password requirements
    if (!passwordValid) {
      toast.error("Password does not meet security requirements", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create new user account
      const response = await fetch(process.env.REACT_APP_API_URL + "/user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, email, password }),
      });

      const result = await response.json();

      if (response.ok) {
        toast("Account created successfully!", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        // Switch to login form after successful signup
        document.querySelector(".login-container").classList.remove("active");
      } else {
        toast.error(result.message || "Signup failed. Please try again.", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred during signup. Please try again.", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles user login form submission
   * Authenticates user and redirects based on company profile status
   */
  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    // Validate email format
    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      return;
    }

    setLoginStatus("logging");

    try {
      // Authenticate user
      const response = await fetch(process.env.REACT_APP_API_URL + "/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();
      if (response.ok) {
        login(result.Token);
        setErrorMessage("");
        setLoginStatus("success");
        
        // Main goal: Navigate to portal (management dashboard)
        // First, check if user has a company profile
        try {
          const profileResponse = await fetch(
            process.env.REACT_APP_API_URL + "/company/get-company-profile",
            {
              headers: {
                Authorization: `Bearer ${result.Token}`,
              },
            }
          );

          if (profileResponse.ok) {
            // User has profile -> go directly to management portal
            setTimeout(() => {
              navigate("/management");
            }, 1500);
          } else {
            // No profile found -> redirect to create company profile first
            setTimeout(() => {
              navigate("/new-company");
            }, 1500);
          }
        } catch (profileError) {
          console.error("Error checking profile:", profileError);
          // On error, redirect to new company profile creation to be safe
          setTimeout(() => {
            navigate("/new-company");
          }, 1500);
        }
      } else {
        setErrorMessage(
          result.message || "Login failed. Please check your credentials."
        );
        setLoginStatus("idle");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred during login. Please try again.", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
      setLoginStatus("idle");
    }
  };

  /**
   * Effect to handle login/signup form toggle functionality
   * Sets up event listeners for toggle buttons
   */
  useEffect(() => {
    const container = document.querySelector(".login-container");
    const registerBtn = document.querySelector(".toggle-signup-btn");
    const loginBtn = document.querySelector(".toggle-login-btn");

    if (registerBtn && loginBtn && container) {
      const handleRegisterClick = () => container.classList.add("active");
      const handleLoginClick = () => container.classList.remove("active");

      registerBtn.addEventListener("click", handleRegisterClick);
      loginBtn.addEventListener("click", handleLoginClick);

      return () => {
        registerBtn.removeEventListener("click", handleRegisterClick);
        loginBtn.removeEventListener("click", handleLoginClick);
      };
    }
  }, []);

  /**
   * Handles multiple clicks on login title to access admin panel
   * Requires 4 clicks within 400ms to navigate to admin login
   */
  const handleClick = () => {
    setClickCount((prev) => {
      const newCount = prev + 1;

      // Clear timeout if it's still counting
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // If quad-clicked, navigate to admin login
      if (newCount === 4) {
        navigate("/admin/login");
        return 0; // reset click count
      }

      // Reset count after 400ms of inactivity
      timeoutRef.current = setTimeout(() => {
        setClickCount(0);
      }, 400);

      return newCount;
    });
  };
  return (
    <>
      <div className="login-page">
        <ToastContainer
          position="top-right"
          autoClose={800}
          hideProgressBar={false}
          newestOnTop={true}
          transition={Zoom}
        />
        <div className="login-container">
          {/* Login Form */}
          <div className="form-box login">
            <form onSubmit={handleLoginSubmit}>
              <h1 onClick={handleClick}>Login</h1>
              <div className="input-box">
                <input
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                />
                <i className="bx bxs-user"></i>
              </div>
              <div className="input-box">
                <input
                  type="password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrorMessage("");
                  }}
                  placeholder="Password"
                  required
                />

                <i className="bx bxs-lock-alt"></i>
              </div>

              <div className="forgot-link">
                <Link to="/reset-password">Forgot the password?</Link>
              </div>
              <button
                type="submit"
                className="login-btn"
                disabled={
                  loginStatus === "logging" || loginStatus === "success"
                }
              >
                {loginStatus === "logging"
                  ? "Authenticating..."
                  : loginStatus === "success"
                  ? "Login Successful"
                  : "Sign In"}
              </button>
              <div className="error-message-col">
                <span>{errorMessage}</span>
              </div>
            </form>
          </div>

          {/* Signup Form */}
          <div className="form-box signup">
            <form onSubmit={handleSubmit}>
              <h1>Signup</h1>
              <div className="input-box">
                <input
                  type="text"
                  placeholder="Username"
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
                <i className="bx bxs-user"></i>
              </div>
              <div className="input-box">
                <input
                  type="email"
                  placeholder="Email"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <i className="bx bxs-envelope"></i>
              </div>
              <div className="input-box">
                <input
                  type="password"
                  placeholder="Password"
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  required
                />
                <i className="bx bxs-lock-alt"></i>
              </div>

              {/* Password Requirements */}
              {passwordFocused && (
                <div className="password-requirements">
                  <p>Password must:</p>
                  <ul>
                    <li className={minLength ? "valid" : "invalid"}>
                      <i
                        className={`bx ${
                          minLength ? "bxs-check-circle" : "bxs-x-circle"
                        }`}
                      ></i>
                      Must 8 chars
                    </li>
                    <li className={hasUppercase ? "valid" : "invalid"}>
                      <i
                        className={`bx ${
                          hasUppercase ? "bxs-check-circle" : "bxs-x-circle"
                        }`}
                      ></i>
                      A-Z
                    </li>
                    <li className={hasLowercase ? "valid" : "invalid"}>
                      <i
                        className={`bx ${
                          hasLowercase ? "bxs-check-circle" : "bxs-x-circle"
                        }`}
                      ></i>
                      a-z
                    </li>
                    <li className={hasNumber ? "valid" : "invalid"}>
                      <i
                        className={`bx ${
                          hasNumber ? "bxs-check-circle" : "bxs-x-circle"
                        }`}
                      ></i>
                      0-9
                    </li>
                    <li className={hasSpecialChar ? "valid" : "invalid"}>
                      <i
                        className={`bx ${
                          hasSpecialChar ? "bxs-check-circle" : "bxs-x-circle"
                        }`}
                      ></i>
                      !@#
                    </li>
                  </ul>
                </div>
              )}

              <button
                type="submit"
                className={`signup-btn ${
                  !passwordValid || isSubmitting ? "disabled" : ""
                }`}
                disabled={!passwordValid || isSubmitting}
              >
                {isSubmitting ? "Creating account..." : "Create Account"}
              </button>
            </form>
          </div>

          {/* Toggle Box */}
          <div className="toggle-box">
            <div className="toggle-panel toggle-left">
              <h1>Hello! Welcome</h1>
              <p>Don't have an account?</p>
              <button className="toggle-signup-btn">Signup</button>
            </div>
            <div className="toggle-panel toggle-right">
              <h1>Welcome Back</h1>
              <p>Already have an account?</p>
              <button className="toggle-login-btn">Login</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
